import { useCallback, useRef, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import Dialog from "components/dialog";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import {
  hideFeatureDialog,
  selectCurrentPractice,
  selectShowFeatureDialog
} from "features/auth/auth-slice";
import usePurchasePaywallFeature from "features/paywall/use-purchase-paywall-feature.hook";
import { glowShadowGreenButtonSx } from "features/paywall/utils";
import { useGetPaywallFeatureByKeyQuery } from "services/paywall";
import { formatMoney } from "utils/common";

import TrialStatusBanner from "./components/trial-status-banner.component";
import useAmbientScribeTrialStatus from "./use-ambient-scribe-trial-status.hook";

export default function PurchaseAmbientScribeDialog() {
  const theme = useTheme();
  const isLargeHeightScreen = useMediaQuery("(min-height:1024px)");
  const dispatch = useAppDispatch();
  const showFeatureDialog = useAppSelector(selectShowFeatureDialog);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const open = showFeatureDialog?.[AMBIENT_SCRIBE_FEATURE_KEY] || false;
  const [numberOfLicenses, setNumberOfLicenses] = useState<string>("1");
  const {
    data: feature,
    isFetching: isFetchingFeature,
    isLoading: isLoadingFeature
  } = useGetPaywallFeatureByKeyQuery(AMBIENT_SCRIBE_FEATURE_KEY, {
    skip: !open
  });

  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);

  const handleWatchDemo = useCallback(() => {
    setShouldAutoplay(true);
    videoRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const { purchasePaywallFeature, isCreatingFeatureSubscription } =
    usePurchasePaywallFeature();

  const handleClose = useCallback(() => {
    dispatch(hideFeatureDialog(AMBIENT_SCRIBE_FEATURE_KEY));
  }, [dispatch]);

  const handlePurchase = useCallback(() => {
    if (!feature) return;

    purchasePaywallFeature(feature, Number(numberOfLicenses) || 1);
  }, [feature, purchasePaywallFeature, numberOfLicenses]);

  const hasPrice = Number(feature?.price) > 0;
  const hasStripeSetup = Boolean(currentPractice?.stripe_customer_id);
  const isButtonDisabled =
    !numberOfLicenses || Number(numberOfLicenses) < 1 || !hasStripeSetup;

  const tooltipTitle = !hasStripeSetup
    ? "Payment setup required. Please contact Suno support to configure billing for your practice before starting your trial."
    : "";

  const { isTrialExpired, isFetchingSubscriptions } =
    useAmbientScribeTrialStatus();

  return (
    <Dialog
      title="Unlock Ambient Scribes Feature"
      onClose={(event: any, reason: string) => {
        if (reason === "escapeKeyDown" || reason === "backdropClick") {
          return;
        }
        handleClose();
      }}
      open={open}
      maxWidth="sm"
      minWidth={1240}
      dialogTitleSx={{
        pl: "35px",
        background:
          "linear-gradient(90deg,  #065A5C 0%, #2a9e7c 93%) !important",
        color: "white",
        border: 0
      }}
      dialogContentProps={{
        sx: {
          p: 0,
          m: 0,
          background:
            "radial-gradient(circle,rgba(255, 255, 255, 1) 70%, rgba(15, 136, 202, 0.25) 100%)"
        }
      }}
      sx={{
        maxHeight: "100%",
        zIndex: theme.zIndex.snackbar + 101
      }}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          overflowY: "auto",
          mb: 3,
          height: isLargeHeightScreen ? "840px" : "780px",
          mt: 0,
          p: 3,
          pt: 1,
          mx: 2.5,
          borderRadius: "8px"
        }}
      >
        {(isFetchingSubscriptions || isFetchingFeature || isLoadingFeature) && (
          <CircularProgress />
        )}
        {!isFetchingSubscriptions &&
          !isFetchingFeature &&
          !isLoadingFeature && (
            <Box width="100%" height="100%" sx={{ boxSizing: "border-box" }}>
              <TrialStatusBanner featureKey={AMBIENT_SCRIBE_FEATURE_KEY} />
              <Typography
                variant="body3"
                sx={{
                  color: misc.sunoGreen,
                  fontWeight: 700,
                  fontSize: "44px",
                  lineHeight: "50px",
                  display: "flex",
                  justifyContent: "flex-start"
                }}
              >
                See More Patients Daily, Without&nbsp;Working Longer Hours
              </Typography>

              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="flex-start"
                pt={3}
                columnGap={3}
              >
                {/* start */}
                <Box>
                  <Typography
                    variant="body3"
                    sx={{
                      color: neutral[900],
                      fontSize: "24px",
                      lineHeight: "34px"
                    }}
                  >
                    Meet <strong>Ambient Scribe</strong>, your AI&nbsp;assistant
                    for&nbsp;effortless documentation. Record your visits{" "}
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      once — Scribe
                    </Box>{" "}
                    will&nbsp;transcribe, summarize, and&nbsp;generate
                    ready-to-use SOAP Notes for you. Save&nbsp;hours every week
                    and&nbsp;never&nbsp;fall&nbsp;behind on&nbsp;charting again.
                  </Typography>

                  <Stack mt={3} rowGap={1.3}>
                    <Stack direction="row" columnGap={1} alignItems="center">
                      <CheckIcon
                        sx={{
                          color: misc.sunoGreen,
                          fontWeight: 700,
                          fontSize: 28
                        }}
                      />
                      <Typography
                        variant="body3"
                        sx={{
                          color: neutral[900],
                          fontSize: "22px",
                          fontWeight: 700
                        }}
                      >
                        Insurance-ready notes in SOAP or custom template
                      </Typography>
                    </Stack>

                    <Stack direction="row" columnGap={1} alignItems="center">
                      <CheckIcon
                        sx={{
                          color: misc.sunoGreen,
                          fontWeight: 700,
                          fontSize: 28
                        }}
                      />
                      <Typography
                        variant="body3"
                        sx={{
                          color: neutral[900],
                          fontSize: "22px",
                          fontWeight: 700
                        }}
                      >
                        Specifically Built for audiology workflows
                      </Typography>
                    </Stack>

                    <Stack direction="row" columnGap={1} alignItems="center">
                      <CheckIcon
                        sx={{
                          color: misc.sunoGreen,
                          fontWeight: 700,
                          fontSize: 28
                        }}
                      />
                      <Typography
                        variant="body3"
                        sx={{
                          color: neutral[900],
                          fontSize: "22px",
                          fontWeight: 700
                        }}
                      >
                        Never fall behind on documentation again
                      </Typography>
                    </Stack>

                    <Stack direction="row" columnGap={1} alignItems="center">
                      <CheckIcon
                        sx={{
                          color: misc.sunoGreen,
                          fontWeight: 700,
                          fontSize: 28
                        }}
                      />
                      <Typography
                        variant="body3"
                        sx={{
                          color: neutral[900],
                          fontSize: "22px",
                          fontWeight: 700
                        }}
                      >
                        HIPAA-compliant and secure
                      </Typography>
                    </Stack>

                    <Stack direction="row" columnGap={1} alignItems="center">
                      <CheckIcon
                        sx={{
                          color: misc.sunoGreen,
                          fontWeight: 700,
                          fontSize: 28
                        }}
                      />
                      <Typography
                        variant="body3"
                        sx={{
                          color: neutral[900],
                          fontSize: "22px",
                          fontWeight: 700
                        }}
                      >
                        More patients, same work hours
                      </Typography>
                    </Stack>

                    <Link
                      onClick={handleWatchDemo}
                      sx={{
                        mt: 0.5,
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        color: "primary.main",
                        "&:hover": {
                          textDecoration: "underline"
                        },
                        textAlign: "center"
                      }}
                    >
                      <Stack
                        direction="row"
                        columnGap={1}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          background: misc.suno,
                          color: "white",
                          px: 2,
                          py: 0.5,
                          ml: 4,
                          borderRadius: "8px",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.9
                          }
                        }}
                      >
                        <Typography variant="body2" fontWeight={700} pb={0.5}>
                          Watch Now: Ambient Scribe Demo
                        </Typography>
                        <Box pt={0.25}>
                          <PlayCircleIcon />
                        </Box>
                      </Stack>
                    </Link>

                    <Box>
                      <Typography
                        variant="body3"
                        sx={{
                          color: neutral[900],
                          fontSize: "24px",
                          lineHeight: "34px"
                        }}
                      >
                        {!isTrialExpired && (
                          <Box component="span">
                            Start{" "}
                            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                              free for 30 days.
                            </Box>{" "}
                          </Box>
                        )}
                        Suno Scribe listens to you and your patient, so you can
                        be fully present — not scrambling to catch up on notes.
                        Save 47 minutes a day and see 2-3 more patients per week
                        without&nbsp;staying&nbsp;late.
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      justifyContent="flex-start"
                      width="100%"
                      sx={{ mt: 2 }}
                    >
                      <Box sx={{ mr: 3 }}>
                        <Tooltip
                          title={tooltipTitle}
                          arrow
                          disableInteractive
                          placement="top"
                          PopperProps={{
                            sx: {
                              zIndex: theme.zIndex.snackbar + 102
                            }
                          }}
                        >
                          <span>
                            <LoadingButton
                              variant="contained"
                              type="submit"
                              sx={{
                                ...glowShadowGreenButtonSx,

                                fontSize: "20px",
                                minHeight: "58px"
                              }}
                              disabled={isButtonDisabled}
                              endIcon={<NavigateNextIcon />}
                              loading={isCreatingFeatureSubscription}
                              onClick={handlePurchase}
                              className="animate__animated animate__bounceIn "
                            >
                              <Box
                                component="span"
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                {isTrialExpired
                                  ? "Purchase Subscription"
                                  : "Start Your Free 30-Days Trial"}
                              </Box>
                            </LoadingButton>
                          </span>
                        </Tooltip>
                      </Box>

                      <Box
                        sx={{
                          mt: -2,
                          background: "rgba(15, 136, 202, 0.07)",
                          p: 1,
                          px: 2,
                          borderRadius: "8px"
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Number of&nbsp;Licenses
                        </Typography>
                        <TextField
                          type="number"
                          inputProps={{
                            min: 1,
                            max: 999
                          }}
                          disabled={!hasStripeSetup}
                          value={numberOfLicenses}
                          onChange={(e) => {
                            const value = e.target.value;
                            setNumberOfLicenses(value);
                          }}
                          sx={{
                            mt: 1,
                            "& .MuiOutlinedInput-root": {
                              "& input": {
                                color: misc.sunoGreen,
                                fontSize: "20px",
                                padding: "4px 8px"
                              }
                            }
                          }}
                        />
                      </Box>
                      {hasPrice && (
                        <Box
                          sx={{
                            mt: -2,
                            background: "rgba(15, 136, 202, 0.07)",
                            p: 1,
                            px: 2,
                            borderRadius: "8px",
                            minWidth: "155px",
                            ml: 3
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, mb: 1 }}
                          >
                            Monthly Subscription
                          </Typography>
                          <Typography
                            variant="body3"
                            sx={{
                              color: misc.sunoGreen,
                              fontSize: "20px"
                            }}
                          >
                            {formatMoney(
                              Number(numberOfLicenses) *
                                Number(feature?.price || "99")
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                    <Typography
                      variant="body3"
                      sx={{
                        mt: 3,
                        fontWeight: 700,
                        fontSize: "24px",
                        lineHeight: 1.2,
                        display: "flex",
                        justifyContent: "flex-start"
                      }}
                    >
                      Ambient Scribe Video Demo
                    </Typography>
                    <Box
                      ref={videoRef}
                      sx={{
                        position: "relative",
                        paddingBottom: "56.25%",
                        height: 0,
                        overflow: "hidden",
                        borderRadius: "12px"
                      }}
                    >
                      <iframe
                        src={`https://www.loom.com/embed/79d043d6170e4daea971cae88b3af60b${shouldAutoplay ? "?autoplay=1" : ""}`}
                        frameBorder="0"
                        allowFullScreen
                        allow="autoplay"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          borderRadius: "12px"
                        }}
                        title="Ambient Scribe Demo"
                      />
                    </Box>
                  </Stack>
                </Box>
                {/* end */}
              </Stack>
            </Box>
          )}
      </Box>
    </Dialog>
  );
}
